import { Routes } from '../enums';
import type { Location } from 'react-router';

const AUTH_REDIRECT_PARAM = 'redirectTo';

const PUBLIC_AUTH_PATHS = new Set<string>([
  Routes.PUBLIC_CHAT,
  Routes.LANDING,
  Routes.SIGNIN,
  Routes.SIGNUP,
  Routes.SIGNUP_SUCCESS,
  Routes.GOOGLE_CALLBACK,
  Routes.FORGET_PASSWORD,
  Routes.RESET_PASSWORD,
]);

type RedirectLocation = Pick<Location, 'pathname' | 'search' | 'hash'>;
type AuthRedirectState = {
  from?: RedirectLocation | null;
};

export const getLocationPath = (location: RedirectLocation): string => {
  return `${location.pathname}${location.search}${location.hash}`;
};

export const getPublicAuthRedirectTarget = (location: RedirectLocation) => {
  // A target already in flight wins over the path we happen to be on: a link can be bounced
  // through an intermediate auth path, and overwriting the parameter there would strand the
  // user on the landing page instead of the ticket, invoice or page they set out to open.
  const inFlight = new URLSearchParams(location.search).get(AUTH_REDIRECT_PARAM);

  const searchParams = new URLSearchParams({
    [AUTH_REDIRECT_PARAM]: inFlight || getLocationPath(location),
  });

  return {
    pathname: Routes.PUBLIC_CHAT,
    search: `?${searchParams.toString()}`,
  };
};

export const getPostAuthRedirectPath = (
  location: Pick<Location, 'search' | 'state'>,
  fallback = Routes.AI_CHAT
): string => {
  const searchParams = new URLSearchParams(location.search);
  const queryRedirect = searchParams.get(AUTH_REDIRECT_PARAM);
  const state = location.state as AuthRedirectState | null;
  const stateRedirect = state?.from ? getLocationPath(state.from) : undefined;

  return sanitizeAuthRedirectPath(queryRedirect || stateRedirect, fallback);
};

/**
 * Ziel direkt nach einer Neuregistrierung. Ein neu angelegtes Konto hat noch nichts, wohin
 * ein mitgeführtes `redirectTo`/`from` führen könnte (z. B. ein Deep-Link auf das Profil,
 * der den Besucher vor dem Registrieren ins öffentliche Chat-Fenster umgeleitet hat). Der
 * Einrichtungs-Wizard (SetupModal) lebt im AI-Chat — dort muss die Registrierung landen.
 */
export const getPostRegistrationRedirectPath = (): string => Routes.AI_CHAT;

const sanitizeAuthRedirectPath = (redirectPath: string | undefined | null, fallback: string): string => {
  if (!redirectPath || !redirectPath.startsWith('/') || redirectPath.startsWith('//')) {
    return fallback;
  }

  const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;

  try {
    const url = new URL(redirectPath, origin);

    if (url.origin !== origin || PUBLIC_AUTH_PATHS.has(url.pathname)) {
      return fallback;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
};
